import type { CustomJSONComponentTypes, GuestSiteComponentProps } from './JsonComponent.types';
import React from 'react';
// Only import lightweight constants, not the full configs
import { CASH_FUND_COMPONENT_SCHEMA_URL, CASH_FUND_COMPONENT_TYPE } from '@shared/websiteCustomJsonComponents/CashFundComponent/CashFundComponent.constants';
import { PLACE_TO_STAY_SCHEMA_URL, PLACE_TO_STAY_COMPONENT_TYPE } from '@website-custom-components/PlaceToStay/PlaceToStay.constants';
import { THINGS_TO_DO_SCHEMA_URL, THINGS_TO_DO_COMPONENT_TYPE } from '@website-custom-components/ThingsToDo/ThingsToDo.constants';
import { DEFAULT_THINGS_TO_DO_SCHEMA_URL, DEFAULT_THINGS_TO_DO_COMPONENT_TYPE } from '@website-custom-components/DefaultThingsToDo/DefaultThingsToDo.constants';
import { GENERAL_SCHEDULE_SCHEMA_URL, GENERAL_SCHEDULE_COMPONENT_TYPE } from '@website-custom-components/GeneralSchedule/GeneralSchedule.constants';
import { FORM_DISPLAY_SCHEMA_URL, FORM_DISPLAY_COMPONENT_TYPE } from '@website-custom-components/FormDisplay/FormDisplay.constants';
import { VIP_SCHEMA_URL, VIP_COMPONENT_TYPE } from '@website-custom-components/Vip/Vip.constants';
import { WEATHER_REPORT_SCHEMA_URL, WEATHER_REPORT_COMPONENT_TYPE } from '@website-custom-components/WeatherReport/WeatherReport.constants';
import { FAQ_SCHEMA_URL, FAQ_COMPONENT_TYPE } from '@website-custom-components/Faq/Faq.constants';
import { EVENT_COUNTDOWN_SCHEMA_URL, EVENT_COUNTDOWN_COMPONENT_TYPE } from '@website-custom-components/EventCountdown/EventCountdown.constants';
import { RSVP_BLOCK_SCHEMA_URL, RSVP_BLOCK_COMPONENT_TYPE } from '@website-custom-components/RsvpBlock/RsvpBlock.constants';
import { STAY_BOOKING_ASSISTANT_COMPONENT_TYPE, STAY_BOOKING_ASSISTANT_SCHEMA_URL } from './customComponents/StayBookingAssistant/StayBookingAssistant.constants';
import { GRAPHIC_ACCENT_SCHEMA_URL, GRAPHIC_ACCENT_COMPONENT_TYPE } from '@website-custom-components/GraphicAccent/GraphicAccent.constants';
import { EVENT_DETAILS_SCHEMA_URL, EVENT_DETAILS_COMPONENT_TYPE } from '@website-custom-components/EventDetails/EventDetails.constants';

// Lightweight metadata registry - only contains schemaUrl and componentType
// The guest components are imported dynamically when needed
type ComponentMetadata = Readonly<{
  schemaUrl: string;
  componentType: CustomJSONComponentTypes;
  configExportName: string;
  loadComponent: () => Promise<React.ComponentType<GuestSiteComponentProps>>;
}>;

const COMPONENT_REGISTRY: Readonly<Record<string, ComponentMetadata>> = {
  [CASH_FUND_COMPONENT_SCHEMA_URL]: {
    schemaUrl: CASH_FUND_COMPONENT_SCHEMA_URL,
    componentType: CASH_FUND_COMPONENT_TYPE,
    configExportName: 'CashFundComponentGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/cashFundComponentGuestConfig" */ '@apps/guest/routes/CashFunds/websiteEditorComponent/CashFundComponent.guest.config'
      );
      return configModule.CashFundComponentGuestConfig.guestSiteComponent;
    },
  },
  [PLACE_TO_STAY_SCHEMA_URL]: {
    schemaUrl: PLACE_TO_STAY_SCHEMA_URL,
    componentType: PLACE_TO_STAY_COMPONENT_TYPE,
    configExportName: 'PlaceToStayGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/placeToStayGuestConfig" */ '@website-custom-components/PlaceToStay/PlaceToStay.guest.config');
      return configModule.PlaceToStayGuestConfig.guestSiteComponent;
    },
  },
  [THINGS_TO_DO_SCHEMA_URL]: {
    schemaUrl: THINGS_TO_DO_SCHEMA_URL,
    componentType: THINGS_TO_DO_COMPONENT_TYPE,
    configExportName: 'ThingsToDoGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/thingsToDoGuestConfig" */ '@website-custom-components/ThingsToDo/ThingsToDo.guest.config');
      return configModule.ThingsToDoGuestConfig.guestSiteComponent;
    },
  },
  [DEFAULT_THINGS_TO_DO_SCHEMA_URL]: {
    schemaUrl: DEFAULT_THINGS_TO_DO_SCHEMA_URL,
    componentType: DEFAULT_THINGS_TO_DO_COMPONENT_TYPE,
    configExportName: 'DefaultThingsToDoGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/defaultThingsToDoGuestConfig" */ '@website-custom-components/DefaultThingsToDo/DefaultThingsToDo.guest.config'
      );
      return configModule.DefaultThingsToDoGuestConfig.guestSiteComponent;
    },
  },
  [GENERAL_SCHEDULE_SCHEMA_URL]: {
    schemaUrl: GENERAL_SCHEDULE_SCHEMA_URL,
    componentType: GENERAL_SCHEDULE_COMPONENT_TYPE,
    configExportName: 'GeneralScheduleGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/generalScheduleGuestConfig" */ '@website-custom-components/GeneralSchedule/GeneralSchedule.guest.config');
      return configModule.GeneralScheduleGuestConfig.guestSiteComponent;
    },
  },
  [FORM_DISPLAY_SCHEMA_URL]: {
    schemaUrl: FORM_DISPLAY_SCHEMA_URL,
    componentType: FORM_DISPLAY_COMPONENT_TYPE,
    configExportName: 'FormDisplayGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/formDisplayGuestConfig" */ '@website-custom-components/FormDisplay/FormDisplay.guest.config');
      return configModule.FormDisplayGuestConfig.guestSiteComponent;
    },
  },
  [VIP_SCHEMA_URL]: {
    schemaUrl: VIP_SCHEMA_URL,
    componentType: VIP_COMPONENT_TYPE,
    configExportName: 'VipGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/vipGuestConfig" */ '@website-custom-components/Vip/Vip.guest.config');
      return configModule.VipGuestConfig.guestSiteComponent;
    },
  },
  [WEATHER_REPORT_SCHEMA_URL]: {
    schemaUrl: WEATHER_REPORT_SCHEMA_URL,
    componentType: WEATHER_REPORT_COMPONENT_TYPE,
    configExportName: 'WeatherReportGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/weatherReportGuestConfig" */ '@website-custom-components/WeatherReport/WeatherReport.guest.config');
      return configModule.WeatherReportGuestConfig.guestSiteComponent;
    },
  },
  [FAQ_SCHEMA_URL]: {
    schemaUrl: FAQ_SCHEMA_URL,
    componentType: FAQ_COMPONENT_TYPE,
    configExportName: 'FaqGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/faqGuestConfig" */ '@website-custom-components/Faq/Faq.guest.config');
      return configModule.FaqGuestConfig.guestSiteComponent;
    },
  },
  [EVENT_COUNTDOWN_SCHEMA_URL]: {
    schemaUrl: EVENT_COUNTDOWN_SCHEMA_URL,
    componentType: EVENT_COUNTDOWN_COMPONENT_TYPE,
    configExportName: 'EventCountdownGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/eventCountdownGuestConfig" */ '@website-custom-components/EventCountdown/EventCountdown.guest.config');
      return configModule.EventCountdownGuestConfig.guestSiteComponent;
    },
  },
  [RSVP_BLOCK_SCHEMA_URL]: {
    schemaUrl: RSVP_BLOCK_SCHEMA_URL,
    componentType: RSVP_BLOCK_COMPONENT_TYPE,
    configExportName: 'RsvpBlockGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/rsvpBlockGuestConfig" */ '@website-custom-components/RsvpBlock/RsvpBlock.guest.config');
      return configModule.RsvpBlockGuestConfig.guestSiteComponent;
    },
  },
  [STAY_BOOKING_ASSISTANT_SCHEMA_URL]: {
    schemaUrl: STAY_BOOKING_ASSISTANT_SCHEMA_URL,
    componentType: STAY_BOOKING_ASSISTANT_COMPONENT_TYPE,
    configExportName: 'StayBookingAssistantGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/stayBookingAssistantGuestConfig" */ '@website-custom-components/StayBookingAssistant/StayBookingAssistant.guest.config'
      );
      return configModule.StayBookingAssistantGuestConfig.guestSiteComponent;
    },
  },
  [GRAPHIC_ACCENT_SCHEMA_URL]: {
    schemaUrl: GRAPHIC_ACCENT_SCHEMA_URL,
    componentType: GRAPHIC_ACCENT_COMPONENT_TYPE,
    configExportName: 'GraphicAccentGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/graphicAccentGuestConfig" */ '@website-custom-components/GraphicAccent/GraphicAccent.guest.config');
      return configModule.GraphicAccentGuestConfig.guestSiteComponent;
    },
  },
  [EVENT_DETAILS_SCHEMA_URL]: {
    schemaUrl: EVENT_DETAILS_SCHEMA_URL,
    componentType: EVENT_DETAILS_COMPONENT_TYPE,
    configExportName: 'EventDetailsGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/eventDetailsGuestConfig" */ '@website-custom-components/EventDetails/EventDetails.guest.config');
      return configModule.EventDetailsGuestConfig.guestSiteComponent;
    },
  },
} as const;

/**
 * Lookup function to get guest component by schemaUrl.
 * Only loads the component when actually needed.
 * @param schemaUrl - The schema URL to look up
 * @returns Promise resolving to the component, or undefined if not found
 */
export const getGuestComponent = async (schemaUrl: string | null | undefined): Promise<React.ComponentType<GuestSiteComponentProps> | undefined> => {
  if (!schemaUrl) {
    return undefined;
  }

  const metadata = COMPONENT_REGISTRY[schemaUrl];
  if (!metadata) {
    return undefined;
  }

  // Dynamically import and extract the component only when needed
  return await metadata.loadComponent();
};

/**
 * Check if a schemaUrl is registered (without loading the config).
 * Useful for quick lookups to determine if a component type is available.
 */
export const isGuestComponentRegistered = (schemaUrl: string | null | undefined): boolean => {
  if (!schemaUrl) {
    return false;
  }
  return schemaUrl in COMPONENT_REGISTRY;
};
